import { useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import cardIcon from 'assets/img/card-plus.svg';

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

interface Props {
  onClose: () => void;
  onConfirm: () => void;
}

export default function ModalBonus({ onClose, onConfirm }: Props) {
  const modalContentRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalContentRef, true, onClose);

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={true}
        contentLabel="Reconhecer+"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div
            ref={modalContentRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bonus-modal-title"
            className={`relative w-[324px] border border-solid border-electric-blue bg-oxford-blue rounded-xl`}
          >
            <Button
              color="primary"
              className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center z-10"
              onClick={() => {onClose()}}
              aria-label="Fechar modal"
            >
              <IoIosClose color="white" size={40} aria-hidden="true" />
            </Button>

            <div className="mx-auto relative container overflow-y-auto overflow-x-hidden max-h-[85vh]">
              <div>
                <div className="mb-8 flex h-[80px] justify-center items-center space-x-2 bg-electric-blue rounded-t-xl">
                  <img className="w-10" src={cardIcon} alt="" aria-hidden="true" />
                  <h1 tabIndex={0} id="bonus-modal-title" className="text-lg uppercase block text-center text-black font-bold">Reconhecer+</h1>
                </div>

                <div className="bg-maastricht-blue-3 rounded-lg mx-10 py-4 px-6 mb-5 text-center text-[12px] space-y-5 font-thin">
                  <p tabIndex={0}>Você está prestes a usar o seu Reconhecer+.</p>

                  <p tabIndex={0}>Deseja confirmar essa ação?</p>
                </div>

                <div className="flex justify-center mb-5">
                  <Button onClick={() => {onConfirm()}} color="primary" className="uppercase py-2 text-sm  min-w-[190px]">
                    Confirmar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      />
    </>
  );
}


